import { type Message, type Person, type Thread, messageDeletableBy, threadDeletableBy } from '@eformulieren/model'
import type { Language } from '@eformulieren/engine'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { BusyButton } from '../shell/BusyButton.js'
import { ConfirmQuestion } from '../shell/ConfirmQuestion.js'
import { colorFor } from '../collab/attribution.js'
import { isPersonKey, participantNames } from '../collab/PresenceBar.js'
import { inLanguage } from '../language/text.js'

/**
 * Eén opmerkingdraadje als kaart: het citaat, het gesprek, en wat je ermee kunt (R-73, R-139).
 *
 * Overgenomen uit de CitatenPOC (`src/comments/ThreadCard.tsx`), waar de vorm met NWO is
 * afgestemd (eisenboek 12.9), met drie wijzigingen die uit dit product volgen.
 *
 * **1. De naam komt uit de persoon en de kleur wordt berekend.** In de POC draagt elk bericht een
 * door de client geschreven naam én een kleur. Hier is de auteur een tokensubject dat de pod heeft
 * gestempeld (M4.12a), en de kleur volgt er deterministisch uit (`colorFor`). Kleur is daarbij
 * nooit de enige drager: de naam staat er als tekst bij (R-139).
 *
 * **2. Verwijderen kan alleen wat van uzelf is** (besluit van 2 november 2026, eisenboek 12.45). In de
 * POC gooit iedereen elk draadje weg, hard en zonder spoor. Hier verwijdert u uw eigen bericht, en een
 * draadje alleen als u het begon en niemand anders erin schreef; het bericht van een ander blijft staan,
 * en dat draadje lost u op. Tot die datum kon hier niets weg, met als reden dat de berichten het bewijs
 * zijn waarom er iets is gewijzigd. Dat bewijs ligt bij de revisie: een bevroren stand houdt wat er op
 * het moment van de overdracht stond (R-95, ontwerp 4.5), en het levende document is een werkstand
 * waarin een eigen vergissing weg mag. Verwijderen vraagt eerst een bevestiging (`ConfirmQuestion`),
 * want het gaat voor iedereen tegelijk weg. Oplossen laat de berichten staan; bij een compactie gaat een
 * opgelost draadje mee met de gearchiveerde generatie (4.6, M4.12b). Hoe het in het document gaat staat
 * in `document/deleteComment.ts`, en de pod dwingt dezelfde regel af (`apps/runtime/src/threadGuard.ts`).
 *
 * **3. Het moment staat er als absolute tijd.** De POC rekent er een relatieve tijd van ("2 uur
 * geleden"). Dat leest prettig en het is precies verkeerd in een document waar een revisie een
 * aanwijsbaar moment vastlegt: "2 uur geleden" verandert bij elke hertekening en zegt na een dag
 * niets meer. Een `<time>` met zijn `dateTime` erbij is voor een schermlezer bovendien de vorm die
 * hij kan voorlezen.
 */

const QUOTE_LABEL = { nl: 'Ga naar de tekst', en: 'Go to the text' }
const ORPHANED = { nl: 'tekst verwijderd', en: 'text removed' }
const ORPHANED_EXPLANATION = {
  nl: 'De tekst waar deze opmerking bij hoorde is weg. Het gesprek blijft staan.',
  en: 'The text this comment belonged to is gone. The conversation stays.',
}
const RESOLVE = { nl: 'Oplossen', en: 'Resolve' }
const REOPEN = { nl: 'Opnieuw openen', en: 'Reopen' }
const SETTLED = { nl: 'Opgelost', en: 'Resolved' }
const SETTLED_BY = { nl: 'Opgelost door', en: 'Resolved by' }
const SETTLED_AT = { nl: 'op', en: 'on' }
const REPLY = { nl: 'Reageren', en: 'Reply' }
const PLACE = { nl: 'Reageren', en: 'Reply' }
const PLACING = { nl: 'Bezig met plaatsen…', en: 'Posting…' }
const DELETE = { nl: 'Verwijderen', en: 'Delete' }
const DELETE_MESSAGE_NAME = (moment: string) => ({ nl: `Uw opmerking van ${moment} verwijderen`, en: `Delete your comment of ${moment}` })
const DELETE_THREAD = { nl: 'Draadje verwijderen', en: 'Delete thread' }
const DELETE_MESSAGE_QUESTION = { nl: 'Uw opmerking verwijderen?', en: 'Delete your comment?' }
const DELETE_THREAD_QUESTION = { nl: 'Dit draadje verwijderen?', en: 'Delete this thread?' }
const DELETE_MESSAGE_CONSEQUENCE = {
  nl: 'Ze verdwijnt uit het formulier, voor iedereen die deze sectie kan openen. Een versie die al is ingediend, houdt haar.',
  en: 'It disappears from the form, for everyone who can open this section. A version that has already been submitted keeps it.',
}
const DELETE_THREAD_CONSEQUENCE = {
  nl: 'Het draadje verdwijnt uit het formulier, met de markering in de tekst, voor iedereen die deze sectie kan openen. Een versie die al is ingediend, houdt het.',
  en: 'The thread disappears from the form, with its highlight in the text, for everyone who can open this section. A version that has already been submitted keeps it.',
}
const CANCEL = { nl: 'Annuleren', en: 'Cancel' }
const DELETING = { nl: 'Bezig met verwijderen…', en: 'Deleting…' }
/** De sleutel van Draadje verwijderen naast die van elk bericht, voor de focus na Annuleren. */
const THREAD_KEY = 'draad'

/**
 * De draadjes waarvan het reactieveld de focus krijgt zodra hun kaart er staat (WCAG 2.4.3, audit delen
 * G-20). Buiten React en per pagina: tussen "Opmerking plaatsen" en het moment dat het draadje uit het
 * document (of van de pod) binnenkomt, kan het paneel weg en terug zijn, en dan is een toestand daarin weg.
 */
const PENDING_FOCUS = new Set<string>()

export function focusReplyOf(threadId: string): void {
  PENDING_FOCUS.add(threadId)
  // Staat de kaart er al (een snelle pod, of het draadje was er al), dan meteen.
  const field = document.querySelector<HTMLElement>(`[data-draad-antwoord="${CSS.escape(threadId)}"]`)
  if (field !== null) {
    PENDING_FOCUS.delete(threadId)
    field.focus()
    field.scrollIntoView({ block: 'nearest' })
  }
}

export interface ThreadCardProps {
  readonly draad: Thread
  readonly taal: Language
  /** Staat het anker nog in de tekst? Afgeleid uit de editor en niet uit het draadje. */
  readonly anchored: boolean
  /** Mag deze lezer meedoen aan het gesprek? Een alleen-lezer leest wel en schrijft niet. */
  readonly bewerkbaar: boolean
  /**
   * Of de lezer kan reageren. Standaard gelijk aan `bewerkbaar`; een meelezer reageert wel maar doet
   * niets af (besluit 8 van 30 oktober 2026: opmerkingen plaatsen, langs de pod).
   */
  readonly kanReageren?: boolean
  readonly onAntwoord?: (threadId: string, text: string) => unknown
  readonly onAfdoen?: (threadId: string, settled: boolean) => void
  readonly onJump?: (threadId: string) => void
  /** Hoe een persoon heet in beeld; zonder dit staat het tokensubject er. */
  readonly naamVan?: (person: string) => string
  /** Bovenaan de kaart: in het paneel Opmerkingen de plek waar het draadje hangt, als knop ernaartoe. */
  readonly kopregel?: ReactNode
  /** Wie dit leest, zoals de pod hem stempelt: daaraan ziet de kaart welke berichten van hem zijn. */
  readonly ik?: Person
  /**
   * Een eigen bericht verwijderen, of zonder `messageId` het hele draadje (eisenboek 12.45). Een belofte
   * als het langs de pod gaat (een meelezer): dan wacht de knop erop. Zonder dit geen verwijderknop.
   */
  readonly onVerwijder?: (threadId: string, messageId: string | undefined) => unknown
  /** Na een geslaagde verwijdering, met of het draadje weg is: de kaart staat er dan niet meer om de focus te houden. */
  readonly onVerwijderd?: (threadGone: boolean) => void
}

export function ThreadCard({
  draad: thread,
  taal: language,
  anchored,
  bewerkbaar: editable,
  kanReageren: canReply = editable,
  onAntwoord: onReply,
  onAfdoen: onSettle,
  onJump,
  naamVan: nameOf,
  kopregel: headerLine,
  ik: me,
  onVerwijder: onDelete,
  onVerwijderd: onDeleted,
}: ThreadCardProps): ReactNode {
  const [response, setReply] = useState('')
  /** Welke verwijdering op een bevestiging wacht: een bericht, of het draadje (`bericht` leeg). */
  const [asking, setAsking] = useState<{ readonly bericht: string | undefined } | undefined>(undefined)
  const card = useRef<HTMLLIElement>(null)
  /**
   * Waar de focus heen gaat na Annuleren: de knop waarmee de vraag begon (WCAG 2.4.3). Als sleutel en niet
   * als element: Draadje verwijderen staat niet in beeld zolang de vraag openstaat, en komt daarna als nieuw
   * element terug.
   */
  const focusBack = (key: string): void => {
    requestAnimationFrame(() => card.current?.querySelector<HTMLElement>(`[data-verwijder="${CSS.escape(key)}"]`)?.focus())
  }
  // Meedoen aan het gesprek is ook een eigen opmerking weghalen: wie niet reageert, verwijdert niets.
  const canDelete = canReply && onDelete !== undefined && me !== undefined
  // Het hele draadje als knop alleen bij meer dan één bericht: bij één is Verwijderen bij het bericht hetzelfde.
  const wholeThread = canDelete && me !== undefined && thread.berichten.length > 1 && threadDeletableBy(thread, me)
  const replyId = useId()
  const replyField = useRef<HTMLTextAreaElement>(null)
  const hasReply = canReply && !thread.opgelost
  useEffect(() => {
    if (!hasReply || !PENDING_FOCUS.has(thread.id)) return
    PENDING_FOCUS.delete(thread.id)
    replyField.current?.focus()
    // Boven de vaste voet en niet erachter (WCAG 2.4.11): `scroll-margin-bottom` van het formulier.
    replyField.current?.scrollIntoView({ block: 'nearest' })
  }, [hasReply, thread.id])

  /** Een belofte als de reactie langs de pod gaat (een meelezer): dan staat de knop bezig. */
  const place = (): unknown => {
    const text = response.trim()
    if (text === '') return undefined
    const sent = onReply?.(thread.id, text)
    if (sent instanceof Promise) return sent.then(() => setReply(''))
    setReply('')
    return undefined
  }

  /** Is het draadje weg na deze verwijdering? Het hele draadje, of het enige bericht. */
  function gone(messageId: string | undefined): boolean {
    return messageId === undefined || thread.berichten.length === 1
  }

  return (
    <li
      ref={card}
      // Na een verwijdering die de kaart laat staan, gaat de focus hierheen en niet naar de pagina.
      tabIndex={-1}
      className="ef-draad"
      data-afgedaan={thread.opgelost ? 'ja' : 'nee'}
      data-verankerd={anchored ? 'ja' : 'nee'}
    >
      {headerLine}
      {thread.citaat === '' ? null : (
        <p className="ef-draad__citaat">
          <button
            type="button"
            className="ef-draad__citaatknop"
            onClick={() => onJump?.(thread.id)}
            disabled={!anchored}
          >
            <span className="ef-draad__citaattekst">{`“${thread.citaat}”`}</span>
            <span className="ef-sr-only">{inLanguage(QUOTE_LABEL, language)}</span>
          </button>
          {anchored ? null : (
            // Als tekst en niet als een streep door het citaat: R-139, en een doorgehaald citaat
            // leest een schermlezer gewoon voor zonder te zeggen dat er iets mis is.
            <span className="ef-draad__verweesd" title={inLanguage(ORPHANED_EXPLANATION, language)}>
              {inLanguage(ORPHANED, language)}
            </span>
          )}
        </p>
      )}

      <ol className="ef-draad__berichten">
        {thread.berichten.map((message) => (
          <MessageLine
            key={message.id}
            message={message}
            taal={language}
            {...(canDelete && me !== undefined && messageDeletableBy(message, me)
              ? {
                  onVerwijder: () => setAsking({ bericht: message.id }),
                  // Uitgeschakeld en niet weg zolang een vraag openstaat: dan kan de focus erop terug.
                  bezig: asking !== undefined,
                }
              : {})}
            // Nooit de persoonssleutel (R-139, audit van 30 oktober 2026): zonder naam van de schil
            // "Deelnemer", zoals in de aanwezigheid.
            naam={nameOf?.(message.persoon) ?? (isPersonKey(message.persoon) ? participantNames([message.persoon], language)(message.persoon) : message.persoon)}
          />
        ))}
      </ol>

      {thread.opgelost ? (
        <p className="ef-draad__stand">
          {thread.opgelostDoor !== undefined && thread.opgelostOp !== undefined ? (
            // Wie en wanneer, zoals de pod het stempelde (ontwerp C.3, eisenboek 12.46); een draadje dat
            // vóór dat besluit werd opgelost heeft ze niet en zegt alleen dat het is opgelost.
            <>
              {`${inLanguage(SETTLED_BY, language)} ${
                nameOf?.(thread.opgelostDoor) ??
                (isPersonKey(thread.opgelostDoor) ? participantNames([thread.opgelostDoor], language)(thread.opgelostDoor) : thread.opgelostDoor)
              } ${inLanguage(SETTLED_AT, language)} `}
              <time dateTime={thread.opgelostOp}>{formatTime(thread.opgelostOp, language)}</time>
            </>
          ) : (
            inLanguage(SETTLED, language)
          )}
        </p>
      ) : null}

      {canReply && !thread.opgelost ? (
        <div className="ef-draad__antwoord">
          {/* Een zichtbaar label (WCAG 3.3.2, audit delen G-21), met het id van het draadje erbij, zodat
              de focus na het plaatsen van een opmerking hierheen kan (G-20). */}
          <label className="ef-draad__antwoordlabel" htmlFor={replyId}>
            {inLanguage(REPLY, language)}
          </label>
          <textarea
            ref={replyField}
            id={replyId}
            className="ef-draad__antwoordveld"
            data-draad-antwoord={thread.id}
            rows={2}
            value={response}
            onChange={(event) => setReply(event.target.value)}
          />
        </div>
      ) : null}

      {asking === undefined ? null : (
        <ConfirmQuestion
          kop={inLanguage(gone(asking.bericht) ? DELETE_THREAD_QUESTION : DELETE_MESSAGE_QUESTION, language)}
          consequence={inLanguage(gone(asking.bericht) ? DELETE_THREAD_CONSEQUENCE : DELETE_MESSAGE_CONSEQUENCE, language)}
          proceed={inLanguage(DELETE, language)}
          soort="gevaar"
          cancel={inLanguage(CANCEL, language)}
          busyText={inLanguage(DELETING, language)}
          onProceed={() => {
            const threadGone = gone(asking.bericht)
            const done = (): void => {
              setAsking(undefined)
              // Staat de kaart er nog, dan blijft de focus erin; anders weet het paneel waar hij heen gaat.
              if (!threadGone) requestAnimationFrame(() => card.current?.focus())
              onDeleted?.(threadGone)
            }
            const sent = onDelete?.(thread.id, asking.bericht)
            if (sent instanceof Promise) return sent.then(done)
            done()
            return undefined
          }}
          onCancel={() => {
            setAsking(undefined)
            focusBack(asking.bericht ?? THREAD_KEY)
          }}
        />
      )}

      {asking === undefined && ((canReply && !thread.opgelost) || editable || wholeThread) ? (
        <div className="ef-draad__acties">
          {canReply && !thread.opgelost ? (
            <BusyButton
              className="ef-draad__plaats"
              busyText={inLanguage(PLACING, language)}
              disabled={response.trim() === ''}
              onAction={place}
            >
              {inLanguage(PLACE, language)}
            </BusyButton>
          ) : null}
          {editable ? <button
            type="button"
            className="ef-draad__actie"
            onClick={() => onSettle?.(thread.id, !thread.opgelost)}
          >
            {inLanguage(thread.opgelost ? REOPEN : RESOLVE, language)}
          </button> : null}
          {wholeThread ? (
            <button
              type="button"
              className="ef-draad__actie"
              data-soort="gevaar"
              data-verwijder={THREAD_KEY}
              onClick={() => setAsking({ bericht: undefined })}
            >
              {inLanguage(DELETE_THREAD, language)}
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

interface MessageLineProps {
  readonly message: Message
  readonly taal: Language
  readonly naam: string
  /** Alleen bij een eigen bericht: de knop Verwijderen, die de bevestiging in de kaart opent. */
  readonly onVerwijder?: () => void
  /** Er staat al een vraag open in deze kaart: de knop staat er, maar doet even niets. */
  readonly bezig?: boolean
}

/**
 * Eén bericht: wie, wanneer, wat.
 *
 * De kleurstip is versiering met een `aria-hidden`; de naam ernaast is de informatie. Dat is de
 * enige vorm die R-139 toestaat, en het is dezelfde keuze als in de legenda van de attributie.
 */
function MessageLine({ message, taal: language, naam: name, onVerwijder: onDelete, bezig: busy = false }: MessageLineProps): ReactNode {
  /**
   * De donkere variant en niet de lichte.
   *
   * `AUTHOR_COLORS` heeft er twee per auteur: de donkere is de merkkleur zelf en de lichte is
   * die kleur op 15 procent dekking. Die lichte is gemaakt om achtergrond te zijn onder donkere
   * tekst, en als stip van zes tiende em is hij vrijwel onzichtbaar. Voor een vlak dat zelf de
   * kleur moet dragen is de donkere de juiste.
   */
  const color = colorFor(message.persoon)
  return (
    <li className="ef-draad__bericht">
      <p className="ef-draad__berichtkop">
        <span className="ef-draad__auteur">
          <span
            className="ef-draad__stip"
            aria-hidden="true"
            style={{ '--ef-draadkleur': color.dark } as Record<string, string>}
          />
          {name}
        </span>
        <span className="ef-draad__rechts">
          <time className="ef-draad__moment" dateTime={message.moment}>
            {formatTime(message.moment, language)}
          </time>
          {onDelete === undefined ? null : (
            <button
              type="button"
              className="ef-draad__verwijder"
              data-verwijder={message.id}
              aria-label={inLanguage(DELETE_MESSAGE_NAME(formatTime(message.moment, language)), language)}
              disabled={busy}
              onClick={onDelete}
            >
              {inLanguage(DELETE, language)}
            </button>
          )}
        </span>
      </p>
      <p className="ef-draad__tekst">{message.tekst}</p>
    </li>
  )
}

/**
 * Het moment in de taal van de lezer, of onbewerkt als de omgeving er niets van maakt.
 *
 * `Intl` en geen eigen opmaak: een datum is precies het soort ding dat per taal anders staat, en
 * de `dateTime` in het element houdt de machineleesbare vorm vast.
 */
const MONTHS: Readonly<Record<Language, readonly string[]>> = {
  nl: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
}

/**
 * "30 okt, 13:48" (mockup 06, audit delen G-21), in de tijdzone van de lezer. Met een eigen tabel van
 * maanden en niet met `Intl` voor de maand: de ICU van Node en van de browser verschillen ("okt" of
 * "okt."), en dan zegt dezelfde opmerking op twee plekken iets anders.
 */
export function formatTime(moment: string, language: Language): string {
  const date = new Date(moment)
  if (Number.isNaN(date.getTime())) return moment
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  return `${String(date.getDate())} ${MONTHS[language][date.getMonth()] ?? ''}, ${time}`
}
